import "react-circular-progressbar/dist/styles.css";
import "react-data-grid/lib/styles.css";
import "../dataset-style.scss";

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useHistory, useParams } from "react-router-dom";
import { Can } from "src/helpers/Auth";
import useQueryParams from "src/reusable/Chat/hooks/useQueryParams";
import { filterList } from "src/reusable/Functions";
import {
  getSourceDataRecordsWithEmbeddingCount,
  isDocumentDatasetType,
  useDatasetFields,
  useGetDataset,
  useGetFilterSchema,
  useGetFacets,
  useGetProcessingStatus,
} from "../services/DatasetServices";
import { useNotificationStream } from "src/reusable/hooks/useNotificationStream";
import { useFilterUrlSync } from "src/hooks/useFilterUrlSync";
import { useSortUrlSync } from "src/hooks/useSortUrlSync";
import { usePageUrlSync } from "src/hooks/usePageUrlSync";
import {
  convertBackendSchemaToFilterConfig,
  convertBackendFacetsToFilterOptions,
  getMultiSelectFieldsFromSchema,
  generateSyntheticFacetsForIntegerFields,
  appliedFiltersToFacetParams,
  generateFiltersFromFields,
  BackendFilterSchema,
  BackendFacetsResponse,
} from "../config/datasetFilterConfig";
import {
  appliedFiltersToWhereFilters,
  filtersToUrlParams,
} from "src/utils/filterUrlUtils";
import AppliedFiltersBar from "src/components/filters/AppliedFiltersBar";
import FacetedFilterSidebar from "src/components/filters/FacetedFilterSidebar";

import {
  Box,
  Divider,
  Drawer,
  LinearProgress,
  Stack,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { AxiosError } from "axios";
import { useQuery, useQueryClient } from "react-query";
import { Panel, PanelGroup, PanelResizeHandle } from "react-resizable-panels";
import { useNucAppStore } from "src/stores/NucAppStore";
import { whereFilterType } from "src/types/types";
import { DataChunk } from "src/views/assistant/components/sources/SourceListItem";
import {
  useDatasetViewerStore,
  DrawerComponent,
} from "../stores/DatasetViewerStore";
import { Dataset, SourceDataRecord } from "../types";
import DatasetAccessDenied from "./DatasetAccessDenied";
import DatasetToolBar from "./DatasetToolBar";
import "./DatasetViewer.css";
import DatasetViewerAlerts from "./DatasetViewerAlerts";
import DatasetViewerHeader from "./DatasetViewerHeader";
import DocumentDatasetSearch from "./DocumentDatasetSearch";
import SDRCardList from "./SDRCardList";
import SDRTable from "./SDRTable";
import { deriveDataColumns } from "./datasetColumns";

const NUM_RESULTS = 25;
export interface DatasetField {
  dataset_fk: number;
  id: number;
  updated_date: string;
  field_description: string;
  field_name: string;
  create_date: string;
  pinning: "left" | "right" | null;
  order: number | null;
  is_visible: boolean;
  filter_type_override: string | null;
}

export type SourceDataRecordsApiResponse = {
  data: Array<SourceDataRecord>;
  chunks: Array<DataChunk>;
  meta: {
    totalRowCount: number;
    totalRowCountIsEstimate?: boolean;
    /** Every revision in the dataset, current and superseded; null when filtered, searched, or the server could not count it. */
    totalRevisionCount?: number | null;
    /** The providers the dataset's documents sync from, whatever the filters or page; empty when none. */
    sourceProviders?: { provider: string; label: string }[];
    nextCursor?: number | null;
    prevCursor?: number | null;
    hasMore?: boolean;
  };
};

interface DatasetViewerProps {
  // Mode of operation
  mode?: "standalone" | "embedded";

  // Pre-loaded data for embedded mode
  preloadedData?: {
    sourceDataRecords: SourceDataRecordsApiResponse;
    dataset: Dataset;
    datasetFields: DatasetField[];
    searchText?: string;
    whereFilters?: whereFilterType[];
  };

  // Override dataset ID (for embedded mode routing)
  overrideDatasetId?: string;

  // Callback for row selection in embedded mode
  onRowSelect?: (row: SourceDataRecord) => void;

  // Hide certain UI elements in embedded mode
  hideElements?: {
    header?: boolean;
    uploadButton?: boolean;
    sideDrawer?: boolean;
    downloadButton?: boolean;
  };

  // Initial search and filter values for CR smart search
  initialSearchQuery?: string;
  initialFilterPredicates?: whereFilterType[];

  // Configurable results limit for embedded mode
  maxSearchResults?: number;
  onMaxSearchResultsChange?: (value: number) => void;

  // Callback for data changes
  onDataChange?: (data: SourceDataRecord[], fields: DatasetField[]) => void;
}

const DatasetViewer: React.FC<DatasetViewerProps> = ({
  mode = "standalone",
  preloadedData,
  overrideDatasetId,
  onRowSelect,
  initialSearchQuery,
  initialFilterPredicates,
  maxSearchResults = NUM_RESULTS,
  hideElements,
  onDataChange,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isDesktop = useMediaQuery(theme.breakpoints.up("lg"));
  const history = useHistory();
  const [getQueryParam, _] = useQueryParams();
  const filterParam = getQueryParam("filt");

  const activeDrawerComponents = useDatasetViewerStore(
    (state) => state.activeDrawerComponents,
  );
  const isFiltersActive = activeDrawerComponents.includes(
    DrawerComponent.FILTERS,
  );

  const setWhereFilters = useDatasetViewerStore(
    (state) => state.setWhereFilters,
  );

  const setActiveDrawerComponentsAndShow = useDatasetViewerStore(
    (state) => state.setActiveDrawerComponentsAndShow,
  );

  // Determine initial filters with proper priority: initialFilterPredicates > preloadedData > URL params > empty
  const initialFilters = useMemo(() => {
    if (initialFilterPredicates?.length) {
      return initialFilterPredicates;
    }
    if (preloadedData?.whereFilters?.length) {
      return preloadedData.whereFilters;
    }
    if (filterParam) {
      return filterList(filterParam);
    }
    return [];
  }, [initialFilterPredicates, preloadedData?.whereFilters, filterParam]);

  const whereFilters = useDatasetViewerStore((state) => state.whereFilters);

  const headerHeight = useNucAppStore((state) => state.headerHeight);
  const footerHeight = useNucAppStore((state) => state.footerHeight);
  const datasetViewerHeaderRef = useRef<HTMLDivElement>(null);
  const datasetHeaderHeight = datasetViewerHeaderRef.current
    ? datasetViewerHeaderRef.current.getBoundingClientRect().height
    : 0;

  const containerHeight = useMemo(
    () =>
      `calc(100vh - ${headerHeight}px - ${datasetHeaderHeight}px - ${footerHeight}px)`,
    [headerHeight, datasetHeaderHeight, footerHeight],
  );
  const params = useParams<{
    dataset_id: string;
    folder_id: string;
  }>();

  // Use override dataset ID if provided, otherwise use URL param
  const datasetId = overrideDatasetId || params.dataset_id;
  const folderId = params.folder_id;

  const [searchText, setSearchText] = useState<string>(
    initialSearchQuery ||
      preloadedData?.searchText ||
      getQueryParam("search_text") ||
      "",
  );

  // Pagination state synced with URL (enables back/forward and shareable links)
  const { pagination, setPagination, resetPage } = usePageUrlSync({
    pageSize: maxSearchResults,
  });

  // Sorting state synced with URL
  const { sorting, setSorting, getSortParam } = useSortUrlSync({
    defaultSortField: "create_date",
    defaultSortDirection: "desc",
  });

  // Cursor map for keyset pagination: maps pageIndex -> cursor (last record ID of previous page).
  // Enables O(log n) pagination for sequential "next page" navigation.
  // Falls back to offset-based pagination for arbitrary page jumps.
  const [cursorMap, setCursorMap] = useState<Record<number, number>>({});

  // Conditionally fetch dataset data or use preloaded
  const shouldFetchDataset = true; //mode === "standalone" && !preloadedData;
  const { data: fetchedDataset, error: fetchedDatasetError } =
    shouldFetchDataset
      ? (useGetDataset(datasetId) as {
          data: Dataset | undefined;
          error: unknown;
        })
      : { data: undefined, error: undefined };

  const dataset = preloadedData?.dataset || fetchedDataset;
  // Per-object content-read denial (private dataset the caller has no grant
  // on). Rendered as an access-denied page — with an admin break-glass
  // self-grant — instead of the empty shell an undefined dataset produces.
  const isDatasetAccessDenied =
    !dataset &&
    (fetchedDatasetError as { response?: { status?: number } } | undefined)
      ?.response?.status === 403;
  const isDocumentDataset = isDocumentDatasetType(dataset?.dataset_type?.id);

  // Use card view only for document datasets on mobile/tablet (<1200px)
  // Tabular datasets always use table view regardless of screen size
  const useCardView = !isDesktop && isDocumentDataset;

  // Conditionally fetch dataset fields or use preloaded
  const { data: fetchedDatasetFieldsQuery } = shouldFetchDataset
    ? useDatasetFields(datasetId)
    : { data: undefined };
  const fetchedDatasetFieldsData = fetchedDatasetFieldsQuery as
    | Array<DatasetField>
    | undefined;

  const datasetFieldsData =
    preloadedData?.datasetFields || fetchedDatasetFieldsData;

  const datasetFields = useMemo(() => {
    const excludedFields = new Set([
      "Searchable Sections",
      "extracted metadata",
      "Extracted Metadata",
      "extracted_metadata",
      "source_metadata",
    ]);
    const filteredData = datasetFieldsData?.filter(
      (field) => !excludedFields.has(field.field_name),
    );
    return filteredData;
  }, [datasetFieldsData]);

  // ============================================================================
  // New Amazon-style Filter System
  // ============================================================================

  // Fetch filter schema from backend
  const { data: filterSchemaData, isLoading: isFilterSchemaLoading } =
    useGetFilterSchema(datasetId);
  const filterSchema = filterSchemaData as BackendFilterSchema | undefined;

  // Convert backend schema to frontend filter config
  // Falls back to generating from datasetFields if backend schema not available
  const filterConfig = useMemo(() => {
    // Try backend schema first
    if (filterSchema?.fields && filterSchema.fields.length > 0) {
      return convertBackendSchemaToFilterConfig(filterSchema);
    }
    // Fallback: generate from dataset fields
    if (datasetFields && datasetFields.length > 0) {
      return generateFiltersFromFields(datasetFields);
    }
    return [];
  }, [filterSchema, datasetFields]);

  // Get filterable field names for URL sync
  const filterableFields = useMemo(() => {
    return filterConfig.map((c) => c.fieldName);
  }, [filterConfig]);

  // Use the new URL-synced filter hook
  const {
    appliedFilters,
    toggleFilterValue,
    setTextFilter,
    setRangeFilter,
    setDateRangeFilter,
    removeFilter,
    removeFiltersByField,
    clearFilters: clearAllFilters,
    hasActiveFilters,
    userFilterVersion,
  } = useFilterUrlSync({
    filterConfig,
    filterableFields,
    supportLegacyFormat: true,
  });

  // Get field names that need facet data
  const facetFields = useMemo(() => {
    if (!filterSchema) return [];
    return getMultiSelectFieldsFromSchema(filterSchema);
  }, [filterSchema]);

  // Convert applied filters to params for facets API
  const facetFilterParams = useMemo(() => {
    return appliedFiltersToFacetParams(appliedFilters);
  }, [appliedFilters]);

  // Fetch facet data (counts for each filter option)
  const {
    data: facetsData,
    isLoading: isFacetsLoading,
    refetch: refetchFacets,
  } = useGetFacets(datasetId, facetFields, facetFilterParams, {
    enabled: facetFields.length > 0,
  });

  // Convert backend facets to filter options format
  const facetOptions = useMemo(() => {
    if (!facetsData || !filterSchema) return {};
    const converted = convertBackendFacetsToFilterOptions(
      facetsData as BackendFacetsResponse,
    );
    // Generate synthetic facets for integer fields if needed
    return generateSyntheticFacetsForIntegerFields(filterSchema, converted);
  }, [facetsData, filterSchema]);

  // Convert applied filters to legacy whereFilterType for API calls
  const filtersForApi = useMemo(() => {
    return appliedFiltersToWhereFilters(appliedFilters);
  }, [appliedFilters]);

  // Handler for text filters
  const handleSetTextFilter = useCallback(
    (
      fieldName: string,
      value: string,
      mode: "contains" | "exact" = "contains",
    ) => {
      if (mode === "exact") {
        if (value) {
          toggleFilterValue(fieldName, value);
        } else {
          removeFiltersByField(fieldName);
        }
      } else {
        setTextFilter(fieldName, value);
      }
    },
    [toggleFilterValue, setTextFilter, removeFiltersByField],
  );

  // Handler for boolean filters
  const handleSetBooleanFilter = useCallback(
    (fieldName: string, value: boolean | undefined) => {
      if (value !== undefined) {
        toggleFilterValue(fieldName, String(value));
      } else {
        removeFiltersByField(fieldName);
      }
    },
    [toggleFilterValue, removeFiltersByField],
  );

  // ============================================================================
  // Legacy Filter System (kept for compatibility with existing components)
  // ============================================================================

  // Extended whereFilter type that includes the optional id field
  type ExtendedWhereFilterType = whereFilterType & {
    filter_on: whereFilterType["filter_on"] & { id?: number };
  };

  // Function to enrich filters with correct field IDs
  const enrichFiltersWithFieldIds = useCallback(
    (
      filters: whereFilterType[],
      fields: DatasetField[] | undefined,
    ): ExtendedWhereFilterType[] => {
      if (!filters.length || !fields?.length)
        return filters as ExtendedWhereFilterType[];

      return filters.map((filter) => {
        const extendedFilter = filter as ExtendedWhereFilterType;
        if (extendedFilter.filter_on?.id) {
          // Filter already has ID, return as-is
          return extendedFilter;
        }

        // Find the matching field by name
        const matchingField = fields.find(
          (field) => field.field_name === filter.filter_on?.name,
        );

        if (matchingField) {
          return {
            ...filter,
            filter_on: {
              ...filter.filter_on,
              id: matchingField.id,
            },
          } as ExtendedWhereFilterType;
        }

        // No matching field found, return original filter
        console.warn(
          `No matching field found for filter field name: ${filter.filter_on?.name}`,
        );
        return extendedFilter;
      });
    },
    [],
  );

  // Enrich initial filters with field IDs once dataset fields are available
  const enrichedInitialFilters = useMemo(() => {
    return enrichFiltersWithFieldIds(initialFilters, datasetFields);
  }, [initialFilters, datasetFields, enrichFiltersWithFieldIds]);

  // Track initialization and user modifications
  const hasInitializedFilters = useRef(false);
  const lastAppliedInitialFilters = useRef<string>("");

  const [, setTempWhereFilters] = useState<ExtendedWhereFilterType[]>([]);

  // Initialize filters only once when data is ready and we haven't initialized yet
  useEffect(() => {
    const currentInitialFiltersKey = JSON.stringify(
      initialFilterPredicates || [],
    );
    const shouldInitialize =
      !hasInitializedFilters.current &&
      enrichedInitialFilters.length > 0 &&
      datasetFields?.length;

    if (shouldInitialize) {
      setTempWhereFilters(enrichedInitialFilters);
      setWhereFilters(enrichedInitialFilters as whereFilterType[]);
      hasInitializedFilters.current = true;
      lastAppliedInitialFilters.current = currentInitialFiltersKey;
    }
  }, [
    enrichedInitialFilters,
    datasetFields,
    setWhereFilters,
    initialFilterPredicates,
  ]);

  // Only update when initialFilterPredicates explicitly change to new values (not just dataset fields loading)
  useEffect(() => {
    const currentInitialFiltersKey = JSON.stringify(
      initialFilterPredicates || [],
    );
    const filtersHaveChanged =
      currentInitialFiltersKey !== lastAppliedInitialFilters.current;

    if (
      hasInitializedFilters.current &&
      filtersHaveChanged &&
      initialFilterPredicates?.length
    ) {
      const enrichedPredicates = enrichFiltersWithFieldIds(
        initialFilterPredicates,
        datasetFields,
      );
      setTempWhereFilters(enrichedPredicates);
      setWhereFilters(enrichedPredicates as whereFilterType[]);
      lastAppliedInitialFilters.current = currentInitialFiltersKey;
    }
  }, [
    initialFilterPredicates,
    datasetFields,
    enrichFiltersWithFieldIds,
    setWhereFilters,
  ]);

  // Initialize empty filters if no initial filters provided and we haven't initialized
  useEffect(() => {
    if (
      !hasInitializedFilters.current &&
      datasetFields?.length &&
      (!initialFilterPredicates?.length || initialFilterPredicates.length === 0)
    ) {
      setTempWhereFilters([]);
      setWhereFilters([]);
      hasInitializedFilters.current = true;
      lastAppliedInitialFilters.current = "[]";
    }
  }, [datasetFields, initialFilterPredicates, setWhereFilters]);

  // Conditionally fetch source data records or use preloaded
  // Use new filter system (filtersForApi) as primary, fallback to legacy whereFilters
  const activeFilters = filtersForApi.length > 0 ? filtersForApi : whereFilters;

  const shouldFetchSourceData = true; //mode === "standalone" && !preloadedData;

  // Use cursor for keyset pagination when available (sequential navigation).
  // Cursor only works with default sort (no custom sort) and no search active.
  const isDefaultSort =
    sorting.length === 1 &&
    sorting[0].id === "create_date" &&
    sorting[0].desc === true;
  const currentCursor =
    isDefaultSort && !searchText
      ? (cursorMap[pagination.pageIndex] ?? null)
      : null;

  // Standard useQuery with server-side pagination and sorting
  const sourceDataQuery = useQuery<SourceDataRecordsApiResponse, AxiosError>({
    queryKey: [
      "sourceDataRecords",
      datasetId,
      pagination.pageIndex,
      pagination.pageSize,
      JSON.stringify(activeFilters),
      searchText,
      getSortParam(),
      currentCursor,
    ],
    queryFn: () =>
      getSourceDataRecordsWithEmbeddingCount(
        datasetId,
        pagination.pageIndex,
        pagination.pageSize,
        activeFilters,
        maxSearchResults,
        searchText,
        // Sort param: "-field" for desc, "field" for asc, null for none
        // @ts-expect-error JSX type inference differs from actual JS function signature
        getSortParam(),
        false, // aggregateToSdrs
        false, // useFileNameFilter - always use semantic/vector search
        currentCursor,
      ),
    enabled: shouldFetchSourceData && Boolean(datasetId),
    refetchOnWindowFocus: false,
    keepPreviousData: true,
  });

  // Use preloaded data or fetched data
  const {
    data: sourceDataRecords,
    isLoading: isSourceDataRecordsLoading,
    isFetching: isFetchingSourceDataRecords,
    isError: isSourceDataRecordError,
    error: sourceDataRecordError,
  } = preloadedData
    ? {
        data: preloadedData.sourceDataRecords,
        isLoading: false,
        isFetching: false,
        isError: false,
        error: null,
      }
    : sourceDataQuery;

  const flatData = useMemo(
    () => sourceDataRecords?.data ?? [],
    [sourceDataRecords],
  );

  // Store the nextCursor from each response so the next page can use keyset pagination.
  // IMPORTANT: Use a ref for pageIndex so this effect ONLY fires when sourceDataRecords
  // changes (new data arrives), NOT when pageIndex changes. With keepPreviousData,
  // a pageIndex change would fire this with STALE data, writing the wrong cursor and
  // causing an unnecessary re-render mid-fetch that confuses MRT's pagination state.
  const pageIndexRef = useRef(pagination.pageIndex);
  pageIndexRef.current = pagination.pageIndex;

  useEffect(() => {
    const nextCursor = sourceDataRecords?.meta?.nextCursor;
    if (nextCursor != null) {
      setCursorMap((prev) => {
        const nextPageIndex = pageIndexRef.current + 1;
        if (prev[nextPageIndex] === nextCursor) return prev; // no-op
        return { ...prev, [nextPageIndex]: nextCursor };
      });
    }
  }, [sourceDataRecords]);

  // Prefetch next page for faster navigation
  const queryClient = useQueryClient();

  // Track IDs of records that were recently reprocessed (need polling even if chunks > 0)
  const [reprocessedIds, setReprocessedIds] = useState<Set<number>>(new Set());

  // Callback for when a record is reprocessed - adds to polling list
  const handleReprocess = useCallback((sdrId: number) => {
    setReprocessedIds((prev) => new Set(prev).add(sdrId));
  }, []);

  // Auto-update badges: Poll for status changes on records still processing
  // Include reprocessedIds since they need monitoring even if chunks_with_embedding > 0
  const processingIds = useMemo(() => {
    const fromChunks = flatData
      .filter((r) => (r.chunks_with_embedding || 0) === 0)
      .map((r) => r.id);
    // Merge with reprocessedIds
    return [...new Set([...fromChunks, ...reprocessedIds])];
  }, [flatData, reprocessedIds]);

  const { data: statusUpdates } = useGetProcessingStatus({
    sdrIds: processingIds,
    datasetIds: [datasetId],
    shouldPoll: isDocumentDataset && processingIds.length > 0,
    pollInterval: 5000, // Poll every 5 seconds
    enabled: isDocumentDataset && processingIds.length > 0,
  });

  // Keep facet counts fresh whenever any file in this dataset changes status.
  useNotificationStream(
    "notify:datasets:changed",
    "dataset_changed",
    useCallback(
      (data: Record<string, unknown>) => {
        if (
          data?.action === "sdr_status_changed" &&
          String(data?.dataset_id) === String(datasetId)
        ) {
          queryClient.invalidateQueries(["facets", datasetId]);
        }
      },
      [queryClient, datasetId],
    ),
  );

  // Refresh table when any record finishes processing (completed or failed)
  // Also clear finished records from reprocessedIds
  useEffect(() => {
    if (statusUpdates) {
      const finishedIds = statusUpdates
        .filter((s: { is_processing: boolean }) => !s.is_processing)
        .map((s: { source_data_record_id: number }) => s.source_data_record_id);

      if (finishedIds.length > 0) {
        queryClient.invalidateQueries(["sourceDataRecords", datasetId]);
        // Also refresh facets via the poll path (SSE path is handled by the
        // notify:datasets:changed subscription above, but polling is the
        // fallback when SSE is unhealthy).
        queryClient.invalidateQueries(["facets", datasetId]);

        // Remove finished IDs from reprocessedIds set
        setReprocessedIds((prev) => {
          const next = new Set(prev);
          finishedIds.forEach((id: number) => next.delete(id));
          return next;
        });
      }
    }
  }, [statusUpdates, queryClient, datasetId]);

  // Create a Map for O(1) status lookups by record ID
  const processingStatusMap = useMemo(() => {
    if (!statusUpdates) return new Map();
    return new Map(
      statusUpdates.map((s: { source_data_record_id: number }) => [
        s.source_data_record_id,
        s,
      ]),
    );
  }, [statusUpdates]);

  useEffect(() => {
    // Only prefetch if we have data and more pages exist
    if (!shouldFetchSourceData || !datasetId || !sourceDataRecords) return;

    const totalRowCount = sourceDataRecords?.meta?.totalRowCount ?? 0;
    const totalPages = Math.ceil(totalRowCount / pagination.pageSize);

    // Prefetch only the next page (not 2 ahead) to reduce initial request count.
    // Deep prefetching adds requests that serialize on single-worker dev servers.
    const pagesToPrefetch = [pagination.pageIndex + 1].filter(
      (page) => page < totalPages,
    );

    pagesToPrefetch.forEach((pageIndex) => {
      const prefetchCursor =
        isDefaultSort && !searchText ? (cursorMap[pageIndex] ?? null) : null;
      const queryKey = [
        "sourceDataRecords",
        datasetId,
        pageIndex,
        pagination.pageSize,
        JSON.stringify(activeFilters),
        searchText,
        getSortParam(),
        prefetchCursor,
      ];

      // Only prefetch if not already in cache
      if (!queryClient.getQueryData(queryKey)) {
        queryClient.prefetchQuery({
          queryKey,
          queryFn: () =>
            getSourceDataRecordsWithEmbeddingCount(
              datasetId,
              pageIndex,
              pagination.pageSize,
              activeFilters,
              maxSearchResults,
              searchText,
              // @ts-expect-error JSX type inference differs from actual JS function signature
              getSortParam(),
              false, // aggregateToSdrs
              false, // useFileNameFilter
              prefetchCursor,
            ),
          staleTime: 30000, // Keep prefetched data fresh for 30 seconds
        });
      }
    });
  }, [
    pagination.pageIndex,
    pagination.pageSize,
    datasetId,
    activeFilters,
    searchText,
    getSortParam,
    shouldFetchSourceData,
    sourceDataRecords,
    queryClient,
    maxSearchResults,
    cursorMap, // Must be in deps so prefetch uses the correct cursor after it's stored
  ]);

  // Reset page when filters, search, or sort changes (no history entry)
  // Uses userFilterVersion (increments only on user-initiated filter changes, not
  // on browser back/forward) so that the back button preserves the page correctly.
  // Skip on mount to preserve page from URL (shared links)
  const sortParam = getSortParam();
  const isInitialMount = useRef(true);
  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }
    resetPage();
    setCursorMap({}); // Cursors are invalid when query context changes
  }, [
    userFilterVersion,
    searchText,
    sortParam,
    pagination.pageSize,
    resetPage,
  ]);

  // Generate dynamic column names for tabular datasets
  // Extract from actual data records if datasetFields doesn't have columns configured
  const dynamicColumns = useMemo(
    () => (isDocumentDataset ? [] : deriveDataColumns(datasetFields, flatData)),
    [isDocumentDataset, datasetFields, flatData],
  );

  const totalDBRowCount = sourceDataRecords?.meta?.totalRowCount ?? 0;
  const isEstimateCount =
    sourceDataRecords?.meta?.totalRowCountIsEstimate ?? false;

  // Restore drawer state from URL query params on mount
  useEffect(() => {
    const componentsToShow: DrawerComponent[] = [];

    // Check if search_text exists and has a value
    const searchTextParam = getQueryParam("search_text");
    if (searchTextParam && searchTextParam.trim() !== "") {
      componentsToShow.push(DrawerComponent.SEARCH);
    }

    // Check if filt exists and has filters
    const filtParam = getQueryParam("filt");
    if (filtParam) {
      try {
        const filters = filterList(filtParam);
        if (filters && filters.length > 0) {
          componentsToShow.push(DrawerComponent.FILTERS);
        }
      } catch (e) {
        // Invalid filter param, ignore
      }
    }

    // Set the drawer components if any were found
    if (componentsToShow.length > 0) {
      setActiveDrawerComponentsAndShow(componentsToShow);
    }
  }, []); // Run only on mount

  // Handle row click for cards and table
  const handleRowClick = (record: SourceDataRecord) => {
    // If in embedded mode and onRowSelect callback is provided, use it
    if (mode === "embedded" && onRowSelect) {
      onRowSelect(record);
      return;
    }

    const sourceUid = record.source_uid;
    const sdrID = record.id;

    const targetDatasetId =
      mode === "embedded" && record.dataset_fk
        ? Math.abs(record.dataset_fk)
        : datasetId;

    switch (dataset?.dataset_type?.name) {
      case "document":
      case "ProjectAI": // Add this case to route XER files to document viewer
        if (mode === "embedded") {
          window.open(
            `/datasets/${targetDatasetId}/document/${encodeURIComponent(
              sdrID,
            )}`,
            "_blank",
          );
        } else {
          history.push(
            `/datasets/${targetDatasetId}/document/${encodeURIComponent(sdrID)}`,
          );
        }
        break;
      default:
        if (mode === "embedded") {
          window.open(
            `/datasets/${targetDatasetId}/predictions/${encodeURIComponent(
              sourceUid,
            )}`,
            "_blank",
          );
        } else {
          history.push(
            `/datasets/${targetDatasetId}/predictions/${encodeURIComponent(
              sourceUid,
            )}`,
          );
        }
        break;
    }
  };

  // Handle URL parameter changes (but don't override initialFilterPredicates)
  useEffect(() => {
    if (filterParam && !initialFilterPredicates?.length) {
      const urlFilters = filterList(filterParam);
      setWhereFilters(urlFilters);
      setTempWhereFilters(urlFilters);
    }
  }, [filterParam, initialFilterPredicates]);

  // Notify parent component when data changes
  useEffect(() => {
    if (onDataChange && flatData.length > 0 && datasetFields) {
      onDataChange(flatData, datasetFields);
    }
  }, [flatData, datasetFields, onDataChange]);

  useEffect(() => {
    if (mode === "embedded") return;

    // Start from the current URL so sort/page params (managed by their own
    // hooks) are never overwritten with stale values from a race condition.
    const params = new URLSearchParams(window.location.search);

    // Clear all non-sort/page keys (filter + search), then re-add current ones.
    const currentKeys = Array.from(params.keys());
    for (const key of currentKeys) {
      if (key !== "sort" && key !== "page") {
        params.delete(key);
      }
    }

    // Only add search_text if non-empty (avoids ?search_text= clutter)
    if (searchText) {
      params.set("search_text", searchText);
    }

    // Merge in filter params
    filtersToUrlParams(appliedFilters).forEach((value, key) => {
      params.set(key, value);
    });

    const search = params.toString();
    const pathname = folderId
      ? `/datasets/dataset-folders/${folderId}/dataset/${datasetId}/viewer`
      : `/datasets/${datasetId}/viewer`;

    history.replace({ pathname, search: search ? `?${search}` : "" });
  }, [appliedFilters, searchText, datasetId, folderId, mode]);

  // After all hooks: a per-object 403 replaces the viewer with the
  // access-denied page (admin break-glass self-grant lives there).
  if (isDatasetAccessDenied) {
    return <DatasetAccessDenied datasetId={Number(datasetId)} />;
  }

  return (
    <>
      <Can
        perform="dataset_data:read"
        yes={() => (
          <Box
            data-testid="sdr-page"
            sx={{
              bgcolor: "background.paper",
            }}
          >
            {dataset && (
              <Box ref={datasetViewerHeaderRef} sx={{ pt: isMobile ? 1 : 2 }}>
                <DatasetViewerHeader
                  dataset={dataset}
                  sourceDataRecords={sourceDataRecords}
                />
                <Box sx={{ mt: isMobile ? 0.5 : 1.5, px: isMobile ? 1 : 2 }}>
                  <DatasetToolBar
                    dataset={dataset}
                    overrideDatasetId={overrideDatasetId}
                    hideElements={hideElements}
                    searchText={searchText}
                    onSearchChange={setSearchText}
                  />
                </Box>
                <DatasetViewerAlerts
                  isSourceDataRecordError={isSourceDataRecordError}
                  sourceDataRecordError={sourceDataRecordError}
                />
                <Box sx={{ width: "100%", height: 4, position: "relative" }}>
                  {isFetchingSourceDataRecords && (
                    <LinearProgress
                      sx={{ position: "absolute", width: "100%" }}
                    />
                  )}
                </Box>
                <Divider sx={{ borderStyle: "none" }} />
              </Box>
            )}

            {/* Applied Filters Bar - shows active filters as removable chips */}
            {hasActiveFilters && (
              <AppliedFiltersBar
                filters={appliedFilters}
                onRemove={removeFilter}
                onClearAll={clearAllFilters}
                showIcon={true}
                showClearAll={true}
              />
            )}

            <Box
              sx={{
                height: containerHeight,
                overflow: "hidden",
              }}
            >
              <Stack
                direction="row"
                justifyContent="space-between"
                gap={useCardView ? 0 : 1.5}
                sx={{
                  mb: useCardView ? 0 : 2,
                  width: "100%",
                }}
              >
                {/* On mobile/tablet (<1200px), use card list; on desktop, use table */}
                {useCardView ? (
                  <>
                    <Box
                      sx={{
                        width: "100%",
                        height: containerHeight,
                        overflow: "hidden",
                      }}
                    >
                      {/* Document dataset with search: Use DocumentDatasetSearch */}
                      {isDocumentDataset && searchText ? (
                        <Box
                          sx={{
                            height: "100%",
                            overflowY: "auto",
                            WebkitOverflowScrolling: "touch",
                          }}
                        >
                          <DocumentDatasetSearch
                            datasetId={Number(datasetId)}
                            searchText={searchText}
                            documentData={
                              sourceDataRecords
                                ? [
                                    {
                                      dataChunks:
                                        sourceDataRecords.chunks || [],
                                      sourceDataRecords:
                                        sourceDataRecords.data || [],
                                    },
                                  ]
                                : []
                            }
                            isFetchingSourceDataRecords={
                              isFetchingSourceDataRecords
                            }
                            isSourceDataRecordsLoading={
                              isSourceDataRecordsLoading
                            }
                          />
                        </Box>
                      ) : (
                        /* Card list for all other cases */
                        <SDRCardList
                          records={flatData}
                          isDocumentDataset={isDocumentDataset}
                          onRowClick={handleRowClick}
                          isFetching={isFetchingSourceDataRecords}
                          hasNextPage={false}
                          fetchNextPage={() => {}}
                          variant={isMobile ? "compact" : "comfortable"}
                          totalCount={totalDBRowCount}
                          processingStatusMap={processingStatusMap}
                        />
                      )}
                    </Box>
                    <Drawer
                      anchor="left"
                      open={isFiltersActive}
                      onClose={() => {
                        useDatasetViewerStore
                          .getState()
                          .setShowSideDrawer(false);
                        useDatasetViewerStore
                          .getState()
                          .setActiveDrawerComponents([]);
                      }}
                      PaperProps={{
                        sx: {
                          width: "85%",
                          maxWidth: 360,
                        },
                      }}
                    >
                      {/* Amazon-style faceted filter sidebar */}
                      <FacetedFilterSidebar
                        filterConfig={filterConfig}
                        appliedFilters={appliedFilters}
                        facetData={facetOptions}
                        onToggleValue={toggleFilterValue}
                        onSetRange={setRangeFilter}
                        onSetDateRange={setDateRangeFilter}
                        onSetText={handleSetTextFilter}
                        onSetBoolean={handleSetBooleanFilter}
                        onRemoveFieldFilters={removeFiltersByField}
                        onClearAll={clearAllFilters}
                        onClose={() => {
                          useDatasetViewerStore
                            .getState()
                            .setShowSideDrawer(false);
                          useDatasetViewerStore
                            .getState()
                            .setActiveDrawerComponents([]);
                        }}
                        isLoading={isFilterSchemaLoading}
                        isFacetLoading={isFacetsLoading}
                        onRefreshFacets={() => refetchFacets()}
                        showHeader={true}
                        title="Filter Results"
                      />
                    </Drawer>
                  </>
                ) : (
                  <PanelGroup direction="horizontal" style={{ width: "100%" }}>
                    {/* LEFT PANEL: Amazon-style filter sidebar - ALWAYS visible */}
                    <Panel
                      id="filter-sidebar-panel"
                      order={1}
                      defaultSize={20}
                      minSize={15}
                      maxSize={35}
                    >
                      <Box
                        sx={{
                          height: containerHeight,
                          borderRight: (theme) =>
                            `1px solid ${theme.palette.divider}`,
                        }}
                      >
                        <FacetedFilterSidebar
                          filterConfig={filterConfig}
                          appliedFilters={appliedFilters}
                          facetData={facetOptions}
                          onToggleValue={toggleFilterValue}
                          onSetRange={setRangeFilter}
                          onSetDateRange={setDateRangeFilter}
                          onSetText={handleSetTextFilter}
                          onSetBoolean={handleSetBooleanFilter}
                          onRemoveFieldFilters={removeFiltersByField}
                          onClearAll={clearAllFilters}
                          isLoading={isFilterSchemaLoading || !datasetFields}
                          isFacetLoading={isFacetsLoading}
                          onRefreshFacets={() => refetchFacets()}
                          showHeader={true}
                          title="Filters"
                        />
                      </Box>
                    </Panel>
                    <PanelResizeHandle className="resize-handle">
                      <div className="resize-handle-bar" />
                    </PanelResizeHandle>

                    {/* RIGHT PANEL: Main content (table/search results) */}
                    <Panel
                      id="main-content-panel"
                      order={2}
                      defaultSize={80}
                      minSize={50}
                    >
                      {/* Desktop: Clean sortable table for non-search view */}
                      {(!searchText || !isDocumentDataset) && (
                        <Box sx={{ height: containerHeight }}>
                          <SDRTable
                            records={flatData}
                            isDocumentDataset={isDocumentDataset}
                            dynamicColumns={dynamicColumns}
                            onRowClick={handleRowClick}
                            totalCount={totalDBRowCount}
                            isEstimateCount={isEstimateCount}
                            pagination={pagination}
                            onPaginationChange={setPagination}
                            sorting={sorting}
                            onSortingChange={setSorting}
                            isLoading={isSourceDataRecordsLoading}
                            isFetching={isFetchingSourceDataRecords}
                            processingStatusMap={processingStatusMap}
                            onReprocess={handleReprocess}
                          />
                        </Box>
                      )}
                      {/* Document search: Use DocumentDatasetSearch */}
                      {isDocumentDataset && searchText && (
                        <Box
                          sx={{
                            height: containerHeight,
                            overflowY: "auto",
                            "&::-webkit-scrollbar": {
                              width: "12px",
                              height: "12px",
                            },
                            "&::-webkit-scrollbar-track": {
                              backgroundColor: "#f5f5f5",
                            },
                            "&::-webkit-scrollbar-thumb": {
                              backgroundColor: "#c1c1c1",
                              borderRadius: "8px",
                              border: "2px solid #f5f5f5",
                              "&:hover": {
                                backgroundColor: "#a0a0a0",
                              },
                            },
                          }}
                        >
                          <DocumentDatasetSearch
                            datasetId={Number(datasetId)}
                            searchText={searchText}
                            documentData={
                              sourceDataRecords
                                ? [
                                    {
                                      dataChunks:
                                        sourceDataRecords.chunks || [],
                                      sourceDataRecords:
                                        sourceDataRecords.data || [],
                                    },
                                  ]
                                : []
                            }
                            isFetchingSourceDataRecords={
                              isFetchingSourceDataRecords
                            }
                            isSourceDataRecordsLoading={
                              isSourceDataRecordsLoading
                            }
                          />
                        </Box>
                      )}
                    </Panel>
                  </PanelGroup>
                )}
              </Stack>
            </Box>
          </Box>
        )}
        no={() => <div>You are not authorized to view this dataset.</div>}
      />
    </>
  );
};

export default DatasetViewer;
